/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, createContext, forwardRef } from 'react';
import { type ContextValue, Switch as SwitchCore, type SwitchProps as SwitchCoreProps, type SwitchRenderProps as SwitchRenderPropsCore, useContextProps } from 'react-aria-components';
import { $ } from '../../tokens.stylex';
import type { StyleOrFunction } from '../../types';
import { applyStylesWithRenderProps } from '../../utils/apply-styles-with-render-props/apply-styles-with-render-props.util';
import { resolveComponentStyle } from '../../utils/resolve-component-style/resolve-component-style.util';
import { Label, type LabelProps } from '../label/label.component';

interface SwitchProps extends Omit<SwitchCoreProps, 'style' | 'className'> {
  /** The size variant of the switch. (Default: 20) */
  size?: keyof typeof sizeVariants;
  /** The color variant of the switch when selected. (Default: 'blue') */
  color?: keyof typeof railColorVariants;
  style?: StyleOrFunction<SwitchRenderPropsCore> | {
    base?: StyleOrFunction<SwitchRenderPropsCore>;
    knob?: StyleOrFunction<SwitchRenderPropsCore>;
    rail?: StyleOrFunction<SwitchRenderPropsCore>;
    label?: StyleOrFunction<SwitchRenderPropsCore>;
  };
}

const SwitchContext = createContext<ContextValue<SwitchProps, HTMLLabelElement>>({});

const Switch = forwardRef((props: SwitchProps, ref: ForwardedRef<HTMLLabelElement>) => {
  [props, ref] = useContextProps(props, ref, SwitchContext);

  const { style, size = 20, color = 'blue', ...coreProps } = props;
  let { children } = props;

  return (
    <SwitchCore
      ref={ref}
      {...applyStylesWithRenderProps<SwitchRenderPropsCore>((state) =>
        stylex.props(
          styles.base,
          sizeVariants[size],
          resolveComponentStyle(style, state, 'base'),
        ))}
      {...coreProps}>
      {(renderProps) => {
        children = typeof children === 'function' ? children(renderProps) : children;

        return (
          <>
            <span
              {...stylex.props(
                railStyles.base,
                railSizeVariants[size],
                renderProps.isSelected && railColorVariants[color],
                renderProps.isDisabled && railStyles.disabled,
                resolveComponentStyle(style, renderProps, 'rail')
              )}>
              <span
                {...stylex.props(
                  knobStyles.base,
                  knobSizeVariants[size],
                  renderProps.isSelected && knobStyles.selected,
                  renderProps.isSelected && knobSelectedSizeVariants[size],
                  renderProps.isDisabled && knobStyles.disabled,
                  resolveComponentStyle(style, renderProps, 'knob')
                )}
              />
            </span>
            {children && (
              <Label
                size={LABEL_SIZES[size]}
                style={[labelStyles.base, renderProps.isDisabled && labelStyles.disabled, resolveComponentStyle(style, renderProps, 'label')]}
                color={(
                  (renderProps.isDisabled && 'disabled') ||
                  (renderProps.isSelected || renderProps.isHovered) && 'primary' ||
                  'tertiary'
                )}>
                {children}
              </Label>
            )}
          </>
        );
      }}
    </SwitchCore>
  );
});

const LABEL_SIZES: Record<keyof typeof sizeVariants, LabelProps['size']> = {
  28: 16,
  20: 14,
  12: 12,
};

const styles = stylex.create({
  base: {
    alignItems: 'center',
    cursor: {
      default: 'pointer',
      ':is([data-disabled])': 'not-allowed',
    },
    display: 'inline-flex',
  },
});

const sizeVariants = stylex.create({
  28: {
    gap: 12,
    height: 28,
  },
  20: {
    gap: 8,
    height: 20,
  },
  12: {
    gap: 8,
    height: 12,
  }
});

const railStyles = stylex.create({
  base: {
    alignItems: 'center',
    backgroundColor: $.opacity04,
    borderColor: $.border04,
    borderRadius: 999,
    borderStyle: 'solid',
    borderWidth: 1.5,
    cursor: 'pointer',
    display: 'inline-flex',
    height: '100%',
    position: 'relative',
    transitionDuration: '200ms',
    transitionProperty: 'background-color',
  },
  disabled: {
    backgroundColor: $.opacity04,
    borderColor: $.border04,
    cursor: 'not-allowed',
    opacity: 0.5,
  },
});

const knobStyles = stylex.create({
  base: {
    backgroundColor: $.iconTertiary,
    borderRadius: 999,
    display: 'block',
    height: 20,
    marginLeft: 0,
    transformOrigin: 'right center',
    transitionDuration: '200ms',
    transitionProperty: 'all',
    width: 20,
  },
  selected: {
    backgroundColor: $.iconWhite,
  },
  disabled: {
    backgroundColor: $.iconDisabled,
  }
});

const railSizeVariants = stylex.create({
  28: {
    width: 48,
  },
  20: {
    width: 34,
  },
  12: {
    width: 20,
  }
});

const railColorVariants = stylex.create({
  blue: {backgroundColor: $.surfaceHighlight},
  green: {backgroundColor: $.surfacePositive},
  red: {backgroundColor: $.surfaceCritical}
});

const knobSizeVariants = stylex.create({
  28: {
    height: 18,
    marginLeft: 5,
    width: 18,
  },
  20: {
    height: 12,
    marginLeft: 4,
    width: 12,
  },
  12: {
    height: 6,
    marginLeft: 2,
    width: 6,
  }
});

const knobSelectedSizeVariants = stylex.create({
  28: {
    marginLeft: 23,
  },
  20: {
    marginLeft: 16,
  },
  12: {
    marginLeft: 10,
  }
});

const labelStyles = stylex.create({
  base: {
    cursor: 'pointer',
    transitionDuration: '200ms',
    transitionProperty: 'color',
  },
  disabled: {
    cursor: 'not-allowed',
  }
});

export { Switch, SwitchContext, type SwitchProps };
